import { useCallback, useEffect, useRef } from 'react'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import { getOutputModelAsset } from 'Pods/ThreeDStudio/Shared/Utils/getOutputModelAsset'

import { setGenerationResultPreview } from './actions'
import { isSignedUrlExpired } from './signedUrlExpiry'

// Re-signs a finished run's preview image, for every consumer that renders one (the
// Create with AI dropdown rows and the "model ready" toast). A result's previewUrl is
// persisted to localStorage when the run finishes, but that URL is a CloudFront signature
// with a bounded window (Generator::SignedAssetUrl), so a result left unopened for a few
// hours renders a broken image. This refetches the run - the same endpoint /ai uses to load
// a model - and writes the fresh URL back to the store, which persists it for the next page
// load and hands it to the other consumer of the same result.
//
// Two triggers, one attempt per run: proactively when the persisted URL's `Expires` has
// passed, and reactively on the image's load error (covers a signature invalidated for any
// other reason). Capping at one attempt keeps a still-failing URL from looping, since the
// refreshed value flows back in as a new prop. The cap is keyed on the run rather than on
// the mount: the toast and the dropdown rows stay mounted across finished runs, so a boolean
// guard would spend its single attempt on one run and leave every later broken preview
// unrefreshed until the page reloads.
export default function usePreviewUrlRefresh({ runId, previewUrl }) {
  const { getData } = useApiGet()
  const refreshedRunIdRef = useRef(null)

  const refresh = useCallback(() => {
    if (runId == null || refreshedRunIdRef.current === String(runId)) {
      return
    }
    refreshedRunIdRef.current = String(runId)

    getData({
      path: `/api/internal/generator/runs/${runId}`,
      onSuccess: (response) => {
        const { previewUrl: freshPreviewUrl } = getOutputModelAsset(
          response?.data?.data?.attributes?.outputAssets
        )
        setGenerationResultPreview(runId, freshPreviewUrl)
      },
    })
  }, [getData, runId])

  useEffect(() => {
    if (isSignedUrlExpired(previewUrl)) {
      refresh()
    }
  }, [previewUrl, refresh])

  return refresh
}
